import { AfterViewInit, DestroyRef, Directive, ElementRef, inject, NgZone, Renderer2 } from "@angular/core";

@Directive({
	selector: "form label[for]",
	standalone: true
})
export class RequiredLabelDirective implements AfterViewInit {
	private el = inject(ElementRef<HTMLLabelElement>);
	private renderer = inject(Renderer2);
	private zone = inject(NgZone);
	private destroyRef = inject(DestroyRef);
	private targetObserver: MutationObserver | null = null;
	private formObserver: MutationObserver | null = null;
	private observedTarget: HTMLElement | null = null;
	private isDestroyed = false;

	constructor() {
		this.destroyRef.onDestroy(() => {
			this.isDestroyed = true;
			this.disconnectObservers();
		});
	}

	ngAfterViewInit(): void {
		this.zone.runOutsideAngular(() => {
			setTimeout(() => {
				if (this.isDestroyed) return;
				this.refreshObservedTarget();
				this.observeFormChanges();
				this.updateClass();
			});
		});
	}

	private getTargetElement(): HTMLElement | null {
		const forAttr = this.el.nativeElement.getAttribute("for");
		if (!forAttr) return null;

		const form = this.el.nativeElement.closest("form");
		const formElement = this.getElementFromForm(form, forAttr);
		if (formElement) return formElement;

		return this.el.nativeElement.ownerDocument.getElementById(forAttr);
	}

	private getElementFromForm(form: HTMLFormElement | null, fieldNameOrId: string): HTMLElement | null {
		if (!form) return null;

		const namedItem = form.elements.namedItem(fieldNameOrId);
		if (namedItem instanceof HTMLElement) return namedItem;

		if (namedItem && "item" in namedItem) {
			const firstItem = namedItem.item(0);
			if (firstItem instanceof HTMLElement) return firstItem;
		}

		return null;
	}

	private isRequired(target: HTMLElement): boolean {
		return target.hasAttribute("required")
			|| target.getAttribute("aria-required") === "true";
	}

	private updateClass(): void {
		const target = this.observedTarget ?? this.getTargetElement();
		const shouldShow = target ? this.isRequired(target) : false;

		if (shouldShow) {
			this.renderer.addClass(this.el.nativeElement, "required-label");
		} else {
			this.renderer.removeClass(this.el.nativeElement, "required-label");
		}
	}

	private refreshObservedTarget(): void {
		const target = this.getTargetElement();
		if (this.observedTarget === target) return;

		this.targetObserver?.disconnect();
		this.observedTarget = target;
		if (!target) return;

		this.targetObserver ??= new MutationObserver(() => this.updateClass());
		this.targetObserver.observe(target, {
			attributes: true,
			attributeFilter: ["required", "aria-required"]
		});
	}

	private observeFormChanges(): void {
		const form = this.el.nativeElement.closest("form");
		if (!form) return;

		this.formObserver ??= new MutationObserver(() => {
			this.refreshObservedTarget();
			this.updateClass();
		}); 
		this.formObserver.observe(form, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ["id", "for"]
		});
	}

	private disconnectObservers(): void {
		this.targetObserver?.disconnect();
		this.formObserver?.disconnect();
		this.targetObserver = null;
		this.formObserver = null;
		this.observedTarget = null;
	}
}
